import type { HTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

/**
 * MEDIDOR DE BARRINHAS — a proporção vista de relance, ao lado do número.
 *
 * Referência visual que o dono trouxe (19/09): o painel do RealLead põe seis barrinhas
 * embaixo de cada taxa, e é o detalhe que mais dá cara de painel de operação. Aqui ele existe
 * pelo mesmo motivo do resto deste design system: quem olha 15 vezes por dia lê a FORMA antes
 * de ler o dígito — e comparar linhas de uma tabela por forma é mais rápido do que por número.
 *
 * ### 🔑 Isto é um MEDIDOR, não um semáforo
 *
 * As barras mostram QUANTO, nunca se está bom ou ruim. É uma escolha, não um esquecimento:
 * para "taxa de entrada" ou "página → clique" **não existe régua calibrada** — ninguém sabe
 * ainda se 40% é ótimo ou péssimo neste mercado, porque o produto nunca teve dado suficiente
 * para dizer. Pintar de verde e vermelho seria inventar um julgamento e, pior, um julgamento
 * que o cliente usaria para decidir orçamento.
 *
 * Quando houver régua medida (como já há para lucro por lead: R$ 0,03 normal, 0,05–0,07 bom),
 * a cor entra por `tone`, que é explícito por chamada — nunca deduzido do valor.
 *
 * ### Sem valor não é zero barra
 *
 * `value = null` desenha o trilho vazio com um traço, e não "0%": a mesma regra de todo o
 * resto (invariantes 8 e 12). Zero barras acesas com o trilho normal significaria "medi e deu
 * zero", que é outra coisa.
 */
export interface MeterProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Fração de 0 a 1. `null` = não há o que medir. */
  value: number | null | undefined;
  /** Quantas barrinhas. 5 é o padrão: cada uma vale 20%, que a pessoa lê sem contar. */
  segments?: number;
  /**
   * A cor. `accent` é o padrão (desde out/2026 é o violeta da marca — `brand` é o
   * mesmo tom com o nome novo). `neutral` para quando a barra é só contexto e não
   * deve puxar o olho. `success`/`warning`/`danger` só com régua medida.
   */
  tone?: 'accent' | 'brand' | 'neutral' | 'success' | 'warning' | 'danger';
  /** O que este medidor mede, para quem usa leitor de tela. */
  label: string;
  size?: 'sm' | 'md';
}

const TONE: Record<NonNullable<MeterProps['tone']>, string> = {
  accent: 'bg-accent',
  brand: 'bg-primary',
  neutral: 'bg-muted-foreground',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
};

export function Meter({
  value,
  segments = 5,
  tone = 'accent',
  label,
  size = 'md',
  className,
  ...props
}: MeterProps) {
  const temDado = value !== null && value !== undefined && Number.isFinite(value);
  const fracao = temDado ? Math.min(1, Math.max(0, value)) : 0;
  /**
   * `ceil`, e não `round`: qualquer fração acima de zero acende pelo menos uma barra. Com
   * `round`, 8% desenharia o medidor vazio — visualmente idêntico a "não houve nada", que é
   * justamente a confusão que este componente não pode criar.
   */
  const acesas = temDado ? (fracao === 0 ? 0 : Math.ceil(fracao * segments)) : 0;

  return (
    <div
      className={cn('inline-flex items-center gap-0.5', className)}
      role="img"
      aria-label={
        temDado ? `${label}: ${Math.round(fracao * 100)}%` : `${label}: sem dados para medir`
      }
      {...props}
    >
      {Array.from({ length: segments }, (_, i) => (
        <span
          key={i}
          aria-hidden="true"
          className={cn(
            'rounded-xs',
            size === 'sm' ? 'h-2 w-1' : 'h-2.5 w-1.5',
            i < acesas ? TONE[tone] : 'bg-border',
            // sem dado: trilho mais apagado ainda, para não competir com o medido
            !temDado && 'opacity-50',
          )}
        />
      ))}
    </div>
  );
}
